import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import apiUrl from "../Apiurl";
import DataTableBase from "../interfaccia/DataTableBase";
import { BlockSkeleton } from "../interfaccia/loading";
import {
  MATCH_BOTTERO_MOTIVI_FILTER_OPTIONS,
  MATCH_BOTTERO_MOTIVI_LABELS,
  MATCH_BOTTERO_READ_ONLY_ADVANCED_FILTER_FIELDS,
  MATCH_BOTTERO_READ_ONLY_DEFAULT_FILTERS,
} from "./MatchBotteroReadOnlyShared";

const PER_PAGE_OPTIONS = [20, 50, 100, 200];

const BS_HEADER_STYLE = { color: "#6b1f2a" };
const VS_HEADER_STYLE = { color: "#163a63" };
const BS_CELL_STYLE = {
  background: "rgba(255, 218, 220, 0.7)",
  borderRadius: "4px",
  padding: "4px 6px",
  display: "inline-block",
  width: "100%",
};
const VS_CELL_STYLE = {
  background: "rgba(217, 236, 255, 0.75)",
  borderRadius: "4px",
  padding: "4px 6px",
  display: "inline-block",
  width: "100%",
};
const MOTIVI_CELL_STYLE = {
  background: "rgba(246, 246, 246, 0.95)",
  borderRadius: "4px",
  padding: "4px 6px",
  display: "inline-block",
  width: "100%",
};

const normalizeSortDirection = (value) =>
  value === "desc" || value === "DESC" ? "desc" : "asc";
const parseEanList = (value) =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
const parseTagList = (value) => {
  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .map((item) => String(item || "").trim())
          .filter(Boolean),
      ),
    );
  }
  const text = String(value || "").trim();
  if (!text) return [];
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) {
      return Array.from(
        new Set(
          parsed
            .map((item) => String(item || "").trim())
            .filter(Boolean),
        ),
      );
    }
  } catch (_error) {}
  return Array.from(
    new Set(
      text
        .split(",")
        .map((item) => String(item || "").trim())
        .filter(Boolean),
    ),
  );
};
const parseMotiviPayload = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch (_error) {
      return null;
    }
  }
  return null;
};
const formatMotiviByDomain = (motivi, key) => {
  if (!motivi || typeof motivi !== "object") return "";
  const raw = motivi[key];
  const values = (Array.isArray(raw) ? raw : raw ? [raw] : [])
    .map((item) => String(item || "").trim())
    .filter(Boolean);
  if (!values.length) return "";
  return values
    .map((code) => MATCH_BOTTERO_MOTIVI_LABELS[code] || code)
    .join(", ");
};
const getMotivoCreazioneCellValue = (row) => {
  return formatMotiviByDomain(row?.motiviParsed, "creazione") || "";
};
const getMotivoIgnoratoCellValue = (row) =>
  formatMotiviByDomain(row?.motiviParsed, "ignore") || "";
const formatTagOptionLabel = (option) => {
  const code = String(option?.code || "").trim();
  const description = String(option?.description || "").trim();
  if (!code) return "";
  return description ? `${code} - ${description}` : code;
};

function MatchBotteroReadOnlyTableSkeleton() {
  return (
    <div className="pa3 overflow-auto">
      <table className="collapse w-100 f7" style={{ minWidth: "1800px" }}>
        <thead>
          <tr>
            {[
              "56px",
              "180px",
              "180px",
              "130px",
              "170px",
              "130px",
              "170px",
              "170px",
              "160px",
              "160px",
              "150px",
              "150px",
            ].map((width, index) => (
              <th
                key={`readonly-head-${index}`}
                className="pa2 bb bordoColAlt tl"
                style={{ width, minWidth: width }}
              >
                <BlockSkeleton tone="soft" width="78%" height={14} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 8 }).map((_, rowIndex) => (
            <tr key={`readonly-row-${rowIndex}`}>
              <td className="pa2 bb bordoColAlt">
                <BlockSkeleton tone="soft" width={24} height={24} />
              </td>
              {Array.from({ length: 11 }).map((__, cellIndex) => (
                <td key={`readonly-cell-${rowIndex}-${cellIndex}`} className="pa2 bb bordoColAlt">
                  <BlockSkeleton
                    tone="soft"
                    width={cellIndex % 3 === 0 ? "74%" : cellIndex % 3 === 1 ? "88%" : "62%"}
                    height={22}
                    borderRadius={4}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MatchBotteroReadOnlyPage({ token }) {
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [totalRows, setTotalRows] = useState(0);
  const [sortBy, setSortBy] = useState("bsArticolo");
  const [sortDirection, setSortDirection] = useState("asc");
  const [filters, setFilters] = useState(MATCH_BOTTERO_READ_ONLY_DEFAULT_FILTERS);
  const [draftFilters, setDraftFilters] = useState(
    MATCH_BOTTERO_READ_ONLY_DEFAULT_FILTERS,
  );
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [tagOptions, setTagOptions] = useState([]);
  const [tagOptionsLoading, setTagOptionsLoading] = useState(false);
  const [tagOptionsError, setTagOptionsError] = useState("");

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token],
  );

  const loadRows = useCallback(async () => {
    if (!token) return;
    setLoadingRows(true);
    setError("");
    try {
      const params = {
        page,
        perPage,
        sortBy,
        sortDirection,
        includeOutOfMovimenti: 1,
      };
      Object.entries(filters).forEach(([key, value]) => {
        if (typeof value === "string" && value.trim().length) {
          params[key] = value.trim();
        }
      });
      const response = await axios.get(`${apiUrl}/moda/match-bottero/list`, {
        headers: authHeaders,
        params,
      });
      const payload = response?.data ?? {};
      const list = Array.isArray(payload.rows) ? payload.rows : [];
      const mapped = list.map((row) => ({
        ...row,
        motiviParsed: parseMotiviPayload(row.motivi),
        tagDaAggiungereParsed: parseTagList(row.tagDaAggiungere),
        rowKey: `${row.codiceArticoloBs}-${row.numeroVarianteBottero}-${row.ordinaleTagliaBottero}`,
      }));
      setRows(mapped);
      const total = Number(payload?.pagination?.total ?? 0);
      setTotalRows(Number.isFinite(total) ? total : 0);
    } catch (err) {
      setError(
        err?.response?.data?.error ??
          err?.message ??
          "Errore caricamento tabella",
      );
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoadingRows(false);
    }
  }, [authHeaders, filters, page, perPage, sortBy, sortDirection, token]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const loadTagOptions = useCallback(async () => {
    if (!token) return;
    setTagOptionsLoading(true);
    setTagOptionsError("");
    try {
      const response = await axios.get(`${apiUrl}/moda/lookup/tag-articolo`, {
        headers: authHeaders,
      });
      setTagOptions(Array.isArray(response?.data?.data) ? response.data.data : []);
    } catch (err) {
      setTagOptions([]);
      setTagOptionsError(
        err?.response?.data?.error ??
          err?.message ??
          "Errore caricamento tag articolo",
      );
    } finally {
      setTagOptionsLoading(false);
    }
  }, [authHeaders, token]);

  useEffect(() => {
    loadTagOptions();
  }, [loadTagOptions]);

  const activeAssignedTagLabel = useMemo(() => {
    const code = String(filters.tagDaAggiungere || "").trim();
    if (!code) return "";
    if (code === "__NONE__") return "Nessun tag";
    const option = tagOptions.find(
      (item) => String(item?.code || "").trim() === code,
    );
    return option ? formatTagOptionLabel(option) : code;
  }, [filters.tagDaAggiungere, tagOptions]);

  const updateDraftFilter = (key, value) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setFilters(draftFilters);
    setPage(1);
  };

  const resetFilters = () => {
    setDraftFilters(MATCH_BOTTERO_READ_ONLY_DEFAULT_FILTERS);
    setFilters(MATCH_BOTTERO_READ_ONLY_DEFAULT_FILTERS);
    setPage(1);
  };

  const renderHeader = (label, type) => (
    <span style={type === "bs" ? BS_HEADER_STYLE : VS_HEADER_STYLE}>
      {label}
    </span>
  );

  const renderEanCell = (value, style) => {
    const list = parseEanList(value);
    if (!list.length) return <span style={style}>-</span>;
    return (
      <div style={{ ...style, display: "block" }}>
        {list.map((ean) => (
          <div key={ean}>{ean}</div>
        ))}
      </div>
    );
  };
  const columns = useMemo(
    () => [
      {
        name: "OK",
        selector: (row) => Number(row.ok) || 0,
        cell: (row) =>
          Number(row.ok) === 1 ? (
            <span className="green b" style={{ fontSize: 18 }}>
              ✓
            </span>
          ) : (
            <span className="dark-gray b" style={{ fontSize: 13 }}>
              ×
            </span>
          ),
        sortable: true,
        sortField: "ok",
        width: "70px",
        center: true,
      },
      {
        name: renderHeader("BS Nome", "bs"),
        selector: (row) => row.bsNome || "",
        cell: (row) => <span style={BS_CELL_STYLE}>{row.bsNome || ""}</span>,
        sortable: true,
        sortField: "bsNome",
        minWidth: "180px",
        wrap: true,
      },
      {
        name: renderHeader("VS Nome", "vs"),
        selector: (row) => row.vsNome || "",
        cell: (row) => <span style={VS_CELL_STYLE}>{row.vsNome || ""}</span>,
        sortable: true,
        sortField: "vsNome",
        minWidth: "180px",
        wrap: true,
      },
      {
        name: renderHeader("BS Articolo", "bs"),
        selector: (row) => row.bsArticolo || "",
        cell: (row) => (
          <span style={BS_CELL_STYLE}>{row.bsArticolo || ""}</span>
        ),
        sortable: true,
        sortField: "bsArticolo",
        minWidth: "130px",
      },
      {
        name: renderHeader("VS Cod produttore", "vs"),
        selector: (row) => row.vsCodProduttore || "",
        cell: (row) => (
          <span style={VS_CELL_STYLE}>{row.vsCodProduttore || ""}</span>
        ),
        sortable: true,
        sortField: "vsCodProduttore",
        minWidth: "170px",
      },
      {
        name: renderHeader("VS Articolo", "vs"),
        selector: (row) => row.vsArticolo || "",
        cell: (row) => (
          <span style={VS_CELL_STYLE}>{row.vsArticolo || ""}</span>
        ),
        sortable: true,
        sortField: "vsArticolo",
        minWidth: "130px",
      },
      {
        name: renderHeader("BS Variante", "bs"),
        selector: (row) => row.bsVariante || "",
        cell: (row) => (
          <span style={BS_CELL_STYLE}>{row.bsVariante || ""}</span>
        ),
        sortable: true,
        sortField: "bsVariante",
        minWidth: "170px",
        wrap: true,
      },
      {
        name: renderHeader("VS Variante", "vs"),
        selector: (row) => row.vsVariante || "",
        cell: (row) => (
          <span style={VS_CELL_STYLE}>{row.vsVariante || ""}</span>
        ),
        sortable: true,
        sortField: "vsVariante",
        minWidth: "170px",
        wrap: true,
      },
      {
        name: renderHeader("BS Brand", "bs"),
        selector: (row) => row.bsBrandConvertito || row.bsBrand || "",
        cell: (row) => (
          <span style={BS_CELL_STYLE}>
            {row.bsBrandConvertito || row.bsBrand || ""}
          </span>
        ),
        sortable: true,
        sortField: "bsBrand",
        minWidth: "160px",
      },
      {
        name: renderHeader("VS Brand", "vs"),
        selector: (row) => row.vsBrand || "",
        cell: (row) => <span style={VS_CELL_STYLE}>{row.vsBrand || ""}</span>,
        sortable: true,
        sortField: "vsBrand",
        minWidth: "160px",
      },
      {
        name: renderHeader("BS Cod modello", "bs"),
        selector: (row) => row.bsCodModello || "",
        cell: (row) => (
          <span style={BS_CELL_STYLE}>{row.bsCodModello || ""}</span>
        ),
        sortable: true,
        sortField: "bsCodModello",
        minWidth: "150px",
      },
      {
        name: renderHeader("VS Cod modello", "vs"),
        selector: (row) => row.vsCodModello || "",
        cell: (row) => (
          <span style={VS_CELL_STYLE}>{row.vsCodModello || ""}</span>
        ),
        sortable: true,
        sortField: "vsCodModello",
        minWidth: "150px",
      },
      {
        name: renderHeader("BS Cod colore", "bs"),
        selector: (row) => row.bsCodColore || "",
        cell: (row) => (
          <span style={BS_CELL_STYLE}>{row.bsCodColore || ""}</span>
        ),
        sortable: true,
        sortField: "bsCodColore",
        minWidth: "130px",
      },
      {
        name: renderHeader("BS Kit (gruppo8)", "bs"),
        selector: (row) => row.bsTabGruppo8 || "",
        cell: (row) => (
          <span style={BS_CELL_STYLE}>{row.bsTabGruppo8 || ""}</span>
        ),
        minWidth: "150px",
      },
      {
        name: renderHeader("VS Cod colore", "vs"),
        selector: (row) => row.vsCodColore || "",
        cell: (row) => (
          <span style={VS_CELL_STYLE}>{row.vsCodColore || ""}</span>
        ),
        sortable: true,
        sortField: "vsCodColore",
        minWidth: "130px",
      },
      {
        name: renderHeader("BS Taglia", "bs"),
        selector: (row) => row.bsTaglia || "",
        cell: (row) => <span style={BS_CELL_STYLE}>{row.bsTaglia || ""}</span>,
        sortable: true,
        sortField: "bsTaglia",
        minWidth: "120px",
      },
      {
        name: renderHeader("Stock", "bs"),
        selector: (row) => (Number(row.bsHaGiacenza) === 1 ? "SI" : "NO"),
        cell: (row) => (
          <span style={BS_CELL_STYLE}>
            {Number(row.bsHaGiacenza) === 1 ? "SI" : "NO"}
          </span>
        ),
        minWidth: "90px",
      },
      {
        name: renderHeader("VS Taglia", "vs"),
        selector: (row) => row.vsTaglia || "",
        cell: (row) => <span style={VS_CELL_STYLE}>{row.vsTaglia || ""}</span>,
        sortable: true,
        sortField: "vsTaglia",
        minWidth: "120px",
      },
      {
        name: "Motivo art-var",
        selector: (row) => formatMotiviByDomain(row.motiviParsed, "artVar"),
        cell: (row) => (
          <span style={MOTIVI_CELL_STYLE}>
            {formatMotiviByDomain(row.motiviParsed, "artVar") || "-"}
          </span>
        ),
        minWidth: "220px",
        wrap: true,
      },
      {
        name: "Motivo taglia",
        selector: (row) => formatMotiviByDomain(row.motiviParsed, "taglia"),
        cell: (row) => (
          <span style={MOTIVI_CELL_STYLE}>
            {formatMotiviByDomain(row.motiviParsed, "taglia") || "-"}
          </span>
        ),
        minWidth: "220px",
        wrap: true,
      },
      {
        name: "Motivo creazione",
        selector: (row) => getMotivoCreazioneCellValue(row),
        cell: (row) => (
          <span style={MOTIVI_CELL_STYLE}>
            {getMotivoCreazioneCellValue(row) || "-"}
          </span>
        ),
        minWidth: "240px",
        wrap: true,
      },
      {
        name: "Motivo ignorato",
        selector: (row) => getMotivoIgnoratoCellValue(row),
        cell: (row) => (
          <span style={MOTIVI_CELL_STYLE}>
            {getMotivoIgnoratoCellValue(row) || "-"}
          </span>
        ),
        minWidth: "240px",
        wrap: true,
      },
      {
        name: "Tag da aggiungere",
        selector: (row) => (row.tagDaAggiungereParsed || []).join(", "),
        cell: (row) => (
          <span style={MOTIVI_CELL_STYLE}>
            {(row.tagDaAggiungereParsed || []).length
              ? row.tagDaAggiungereParsed.join(", ")
              : "-"}
          </span>
        ),
        minWidth: "200px",
        wrap: true,
      },
      {
        name: renderHeader("BS EAN", "bs"),
        selector: (row) => row.bsEanList || "",
        cell: (row) => renderEanCell(row.bsEanList, BS_CELL_STYLE),
        minWidth: "170px",
        wrap: true,
      },
      {
        name: renderHeader("VS EAN", "vs"),
        selector: (row) => row.vsEanList || "",
        cell: (row) => renderEanCell(row.vsEanList, VS_CELL_STYLE),
        minWidth: "170px",
        wrap: true,
      },
    ],
    [],
  );

  return (
    <div className="pa3">
      <div className="sfondoBiancoTema br3 pa3">
        <div className="pa3 sfondoColAltScuro mb3 flex justify-between items-center">
          <div className="f3 b">Art. BS matchati</div>
          <form
            className="flex flex-wrap items-end"
            onSubmit={(e) => {
              e.preventDefault();
              applyFilters();
            }}
          >
            <div className="mr3 mb2">
              <label className="f7 db mb1">BS Articolo</label>
              <input
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.bsArticolo}
                onChange={(e) =>
                  updateDraftFilter("bsArticolo", e.target.value)
                }
              />
            </div>
            <div className="mr3 mb2">
              <label className="f7 db mb1">VS Articolo</label>
              <input
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.vsArticolo}
                onChange={(e) =>
                  updateDraftFilter("vsArticolo", e.target.value)
                }
              />
            </div>
            <div className="mr3 mb2">
              <label className="f7 db mb1">BS Nome</label>
              <input
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.bsNome}
                onChange={(e) => updateDraftFilter("bsNome", e.target.value)}
              />
            </div>
            <div className="mr3 mb2">
              <label className="f7 db mb1">VS Nome</label>
              <input
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.vsNome}
                onChange={(e) => updateDraftFilter("vsNome", e.target.value)}
              />
            </div>
            <div className="mr3 mb2">
              <label className="f7 db mb1">OK</label>
              <select
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.ok}
                onChange={(e) => updateDraftFilter("ok", e.target.value)}
              >
                <option value="">Tutti</option>
                <option value="1">Solo OK</option>
                <option value="0">Solo non OK</option>
              </select>
            </div>
            <div className="mr3 mb2">
              <label className="f7 db mb1">Giacenza BS</label>
              <select
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.hasGiacenza}
                onChange={(e) =>
                  updateDraftFilter("hasGiacenza", e.target.value)
                }
              >
                <option value="">Tutte</option>
                <option value="1">Solo giacenza &gt; 0</option>
                <option value="0">Solo giacenza = 0</option>
              </select>
            </div>
            <div className="mr3 mb2">
              <label className="f7 db mb1">Motivo art-var</label>
              <select
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.motiviArtVar}
                onChange={(e) =>
                  updateDraftFilter("motiviArtVar", e.target.value)
                }
              >
                <option value="">Tutti</option>
                {MATCH_BOTTERO_MOTIVI_FILTER_OPTIONS.motiviArtVar.map((code) => (
                  <option key={code} value={code}>
                    {MATCH_BOTTERO_MOTIVI_LABELS[code] || code}
                  </option>
                ))}
              </select>
            </div>
            <div className="mr3 mb2">
              <label className="f7 db mb1">Motivo taglia</label>
              <select
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.motiviTaglia}
                onChange={(e) =>
                  updateDraftFilter("motiviTaglia", e.target.value)
                }
              >
                <option value="">Tutti</option>
                {MATCH_BOTTERO_MOTIVI_FILTER_OPTIONS.motiviTaglia.map((code) => (
                  <option key={code} value={code}>
                    {MATCH_BOTTERO_MOTIVI_LABELS[code] || code}
                  </option>
                ))}
              </select>
            </div>
            <div className="mr3 mb2">
              <label className="f7 db mb1">Motivo creazione</label>
              <select
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.motiviCreazione}
                onChange={(e) =>
                  updateDraftFilter("motiviCreazione", e.target.value)
                }
              >
                <option value="">Tutti</option>
                {MATCH_BOTTERO_MOTIVI_FILTER_OPTIONS.motiviCreazione.map((code) => (
                  <option key={code} value={code}>
                    {MATCH_BOTTERO_MOTIVI_LABELS[code] || code}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco mb2 mr3"
            >
              Cerca
            </button>
            <button
              type="button"
              className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco mb2 mr3"
              onClick={() => setShowAdvancedFilters(true)}
            >
              Ricerca avanzata
            </button>
            <button
              type="button"
              className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco mb2"
              onClick={resetFilters}
            >
              Reset filtri
            </button>
          </form>
        </div>

        {error ? <div className="dark-red mb3">{error}</div> : null}
        {activeAssignedTagLabel ? (
          <div className="mb3">
            <span className="dib br-pill sfondoColChiaro scrittaDef ph2 pv1 f7">
              Tag assegnato: {activeAssignedTagLabel}
            </span>
          </div>
        ) : null}

        <DataTableBase
          columns={columns}
          data={rows}
          progressPending={loadingRows}
          progressComponent={<MatchBotteroReadOnlyTableSkeleton />}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={PER_PAGE_OPTIONS}
          paginationDefaultPage={page}
          onChangeRowsPerPage={(newPerPage) => {
            setPerPage(newPerPage);
            setPage(1);
          }}
          onChangePage={(newPage) => setPage(newPage)}
          sortServer
          onSort={(column, direction) => {
            setSortBy(column.sortField ?? "bsArticolo");
            setSortDirection(normalizeSortDirection(direction));
            setPage(1);
          }}
          defaultSortField={sortBy}
          defaultSortAsc={sortDirection !== "desc"}
          dense
          highlightOnHover
          keyField="rowKey"
          noDataComponent="Nessuna riga disponibile con i filtri correnti."
        />
      </div>

      {showAdvancedFilters ? (
        <div
          className="fixed top-0 left-0 right-0 bottom-0 z-5"
          style={{ background: "rgba(0, 0, 0, 0.35)" }}
          onClick={() => setShowAdvancedFilters(false)}
        >
          <div
            className="sfondoBiancoTema br3 ba bordoColAlt"
            style={{
              width: "min(1100px, 96vw)",
              maxHeight: "88vh",
              margin: "5vh auto",
              overflow: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pa3">
              <div className="flex items-center justify-between mb3">
                <h3 className="ma0">Ricerca avanzata Art. BS matchati</h3>
                <button
                  type="button"
                  className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco"
                  onClick={() => setShowAdvancedFilters(false)}
                >
                  Chiudi
                </button>
              </div>
              <div className="flex flex-wrap">
                {MATCH_BOTTERO_READ_ONLY_ADVANCED_FILTER_FIELDS.map(([key, label]) => (
                  <div key={key} className="w-100 w-50-m w-third-l pr3 mb3">
                    <label className="f7 db mb1">{label}</label>
                    {Object.prototype.hasOwnProperty.call(
                      MATCH_BOTTERO_MOTIVI_FILTER_OPTIONS,
                      key,
                    ) ? (
                      <select
                        className="input-reset ba bordoColAlt br2 pa2 w-100"
                        value={draftFilters[key] || ""}
                        onChange={(e) => updateDraftFilter(key, e.target.value)}
                      >
                        <option value="">Tutti</option>
                        {MATCH_BOTTERO_MOTIVI_FILTER_OPTIONS[key].map((code) => (
                          <option key={code} value={code}>
                            {MATCH_BOTTERO_MOTIVI_LABELS[code] || code}
                          </option>
                        ))}
                      </select>
                    ) : key === "tagDaAggiungere" ? (
                      <>
                        <select
                          className="input-reset ba bordoColAlt br2 pa2 w-100"
                          value={draftFilters[key] || ""}
                          disabled={tagOptionsLoading}
                          onChange={(e) => updateDraftFilter(key, e.target.value)}
                        >
                          <option value="">Tutti</option>
                          <option value="__NONE__">Nessun tag</option>
                          {tagOptions.map((option) => (
                            <option
                              key={`readonly-tag-${option.code}`}
                              value={option.code}
                            >
                              {formatTagOptionLabel(option)}
                            </option>
                          ))}
                        </select>
                        {tagOptionsError ? (
                          <div className="dark-red f7 mt1">{tagOptionsError}</div>
                        ) : null}
                      </>
                    ) : (
                      <input
                        className="input-reset ba bordoColAlt br2 pa2 w-100"
                        value={draftFilters[key] || ""}
                        onChange={(e) => updateDraftFilter(key, e.target.value)}
                      />
                    )}
                  </div>
                ))}
                <div className="w-100 w-50-m w-third-l pr3 mb3">
                  <label className="f7 db mb1">Giacenza BS</label>
                  <select
                    className="input-reset ba bordoColAlt br2 pa2 w-100"
                    value={draftFilters.hasGiacenza}
                    onChange={(e) =>
                      updateDraftFilter("hasGiacenza", e.target.value)
                    }
                  >
                    <option value="">Tutte</option>
                    <option value="1">Solo giacenza &gt; 0</option>
                    <option value="0">Solo giacenza = 0</option>
                  </select>
                </div>
              </div>
              <div className="mt2 flex flex-wrap items-center">
                <button
                  type="button"
                  className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco mr2 mb2"
                  onClick={() => {
                    applyFilters();
                    setShowAdvancedFilters(false);
                  }}
                >
                  Applica filtri
                </button>
                <button
                  type="button"
                  className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco mb2"
                  onClick={() => {
                    resetFilters();
                    setShowAdvancedFilters(false);
                  }}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default MatchBotteroReadOnlyPage;
